import { User } from '@adtech-web/login-provider-client';
import { addMilliseconds } from 'date-fns';
import { cloneDeep, isUndefined, throttle } from 'lodash';
import type { Poll } from 'schema/poll';
import { ICState } from 'schema/state';
import { isAuthQuestion } from '../guards';
import { PollStorage, PollStoredState } from '../pollStorageTypes';
import { InterpreterEnv, InterpreterIntl } from './interpreter';

// Спецификация логики https://jira.tcsbank.ru/browse/MACP-754
const canRestoreState = (state: Partial<PollStoredState> | null, poll: Poll): state is PollStoredState => {
  if (!state || !state.state) return false;

  const { wave, revision } = poll;

  if (state.wave !== wave) return false;

  return revision === state.revision || !!state.state.finishedAt;
};

const shouldRestoreState = (state: Partial<PollStoredState> | null, poll: Poll): state is PollStoredState => {
  if (!canRestoreState(state, poll)) return false;

  const { useSinglePass, singlePassExpiry } = poll;
  const { finishedAt } = state.state;

  // нет однократного прохождения, опрос не пройден -> восстанавливаем состояние
  // нет однократного прохождения, опрос пройден -> не восстанавливаем состояние (даем пройти еще раз)
  if (!useSinglePass) {
    return !finishedAt;
  }

  if (finishedAt && singlePassExpiry) {
    const canTakeAnotherAttempt = addMilliseconds(finishedAt, singlePassExpiry).getTime() < Date.now();
    return !canTakeAnotherAttempt;
  }

  return true;
};

const getTrackingId = ({ poll, userId }: { poll: Poll; userId: string }) => {
  const { id: pollId } = poll;

  return `${pollId}.${userId}`;
};

const checkRestoreParam = (
  restoreStateQueryParam: string | string[] | undefined,
): restoreStateQueryParam is string | string[] => {
  return !!restoreStateQueryParam && (!Array.isArray(restoreStateQueryParam) || !!restoreStateQueryParam.length);
};

const getUserId = ({
  user,
  variables,
  restoreStateQueryParam,
}: {
  user: User | null;
  variables: Record<string, string> | null;
  restoreStateQueryParam: string | string[] | undefined;
}) => {
  const hasRestoreParam = checkRestoreParam(restoreStateQueryParam);

  if (hasRestoreParam) {
    const queryParams = new URLSearchParams(location.search);

    const paramNameList = Array.isArray(restoreStateQueryParam) ? restoreStateQueryParam : [restoreStateQueryParam];
    const orderedParamList = paramNameList.slice().sort();

    const param = orderedParamList
      .map(paramName => {
        const paramValue = queryParams.get(paramName) || variables?.[paramName];
        if (!paramValue) throw new Error(`Restore state param ${paramName} is not found`);
        return paramValue;
      })
      .join('.');

    return param;
  }

  if (!user?.id) {
    throw new Error('User must be provided');
  }

  return user.id;
};

export interface PollStoragePollClass {
  new (args: { pollId: string }): PollStorage & {
    canEnablePollsSiteWithoutTrackingId(): boolean;
    enablePollsSiteStorage({
      trackingId,
      expireTimeMs,
    }: {
      trackingId: string | null;
      expireTimeMs: number | null;
    }): Promise<void>;
  };
}

/**
 * Возвращает объект storage, предназначенный для работы с состоянием прохождения опроса в хранилище (Polls site или local storage).
 * Отвечает за синхронизацию состояния прохождения опроса с хранилищем
 * @param {object} props - параметры
 * @param {object} props.poll - данные опроса
 * @param {boolean} props.useStorage - флаг, отвечающий за то, нужно ли использовать хранилище
 */
export function createPollStorage({
  poll,
  getUser,
  variables,
  getICState,
  PollStoragePool,
  intl: { TextFieldContentHiddenIntl },
  env: { POLL_STORAGE_UPDATE_TIMEOUT, APP_IC_CONTEXT_URL },
}: {
  PollStoragePool: PollStoragePollClass;
  poll: Poll;
  getICState: () => ICState | undefined;
  getUser: () => User | null;
  env: Pick<InterpreterEnv, 'APP_IC_CONTEXT_URL' | 'POLL_STORAGE_UPDATE_TIMEOUT'>;
  intl: InterpreterIntl;
  variables: Record<string, string> | null;
}): {
  storage: PollStorage;
  beforeAuthStateRestorer: () => Promise<ICState | undefined>;
  persistentStateRestorer: () => Promise<ICState | undefined>;
  updateStorage: (state: ICState, options?: { immediately?: boolean }) => Promise<void>;
  dumpStateToStorage: () => Promise<void>;
} {
  const hasAuth = poll.questions.some(isAuthQuestion);
  const storage = new PollStoragePool({ pollId: poll.id });
  const { singlePassExpiry, restoreStateQueryParam, wave, revision } = poll;

  const switchToPollsSite = async () => {
    let userId: string | null = null;

    try {
      userId = getUserId({ user: getUser(), restoreStateQueryParam, variables });
    } catch (error) {
      console.error(error);
    }

    if (!userId) {
      // MACP-1647 Реализовать отправку referer вместо tracking_id в context со стороны компонентов
      if (checkRestoreParam(restoreStateQueryParam) && storage.canEnablePollsSiteWithoutTrackingId()) {
        await storage.enablePollsSiteStorage({ trackingId: null, expireTimeMs: singlePassExpiry ?? null });
      }

      return;
    }

    const trackingId = getTrackingId({ poll, userId });

    await storage.enablePollsSiteStorage({ trackingId, expireTimeMs: singlePassExpiry ?? null });
  };

  const restoreStateFromStorage = async () => {
    const initialState = await storage.get();

    if (shouldRestoreState(initialState, poll)) {
      return initialState.state;
    }
    return undefined;
  };

  const updateStoredState = async (
    { force = false, isDump = false }: { force?: boolean; isDump?: boolean } = { force: false, isDump: false },
  ) => {
    const actualState = getICState();

    if (!actualState) return;

    // При строгой политике доставки состояние обновляется только тогда, когда были отправлены все системные пиксели.
    // Необходимо для того, чтобы в случае ошибки восстановить состояние, при котором можно было бы повторно ответить на вопрос
    // Проверка isUndefined для обратной совместимости
    const canUpdateStorage = actualState.answersStack.every(
      answerState => !answerState.isAnswered || isUndefined(answerState.isStored) || answerState.isStored,
    );

    if (canUpdateStorage) {
      const storedState = createStoredState(actualState, TextFieldContentHiddenIntl);

      return storage.update({ state: storedState, isDump, revision, wave }, { force });
    }
  };

  const throttledUpdateStorage = throttle(updateStoredState, POLL_STORAGE_UPDATE_TIMEOUT);

  const immediatelyUpdateStorage = async ({ isDump = false }: { isDump: boolean } = { isDump: false }) => {
    throttledUpdateStorage.cancel();
    return updateStoredState({ force: true, isDump });
  };

  /**
   * Немедленно сохраняет дамп состояния прохождения в хранилище.
   * Дамп используется для восстановления состояния после редиректа с сервиса авторизации
   */
  const dumpStateToStorage = () => immediatelyUpdateStorage({ isDump: true });

  return {
    storage,
    beforeAuthStateRestorer: async () => {
      const state = await storage.get();
      if (state?.state && state.isDump) {
        await storage.remove();
        return state.state;
      }
      return undefined;
    },
    persistentStateRestorer: async () => {
      const shouldUsePollsSite = !!APP_IC_CONTEXT_URL && ((!!getUser() && hasAuth) || !!restoreStateQueryParam);

      if (shouldUsePollsSite) {
        await switchToPollsSite();
      }

      return restoreStateFromStorage();
    },
    updateStorage: async (state, { immediately }: { immediately?: boolean } = {}) => {
      if (!state || !state.startedAt) return;

      if (state.finishedAt || immediately) {
        return immediatelyUpdateStorage();
      }
      return throttledUpdateStorage();
    },
    dumpStateToStorage,
  };
}

function createStoredState(state: ICState, TextFieldContentHiddenIntl: string) {
  const storedState = cloneDeep(state);

  if (!storedState.finishedAt && Array.isArray(storedState.answersStack)) {
    for (const answerState of storedState.answersStack) {
      if (answerState.answeredOthers && answerState.isAnswered) {
        answerState.answeredOthers = TextFieldContentHiddenIntl;
      }
    }
  }

  return storedState;
}
